import React, {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "@natera/form";

import { SpinnerView, CodeInput, Progress } from "@app/components";
import {
  IntlContext,
  ServiceContext,
  SignUpContext,
  UserContext,
  OtpVerificationProvider,
  useOtpVerificationContext,
  ConfigContext,
} from "@app/provider";
import { getInitialMode } from "@app/utils/otpStorageKeys";
import { VerificationMessageRes } from "@app/provider/signUp";
import {
  RecaptchaTokens,
  useRecaptchaSubmit,
  usePortalRedirects,
  useTokenSourceFromQuery,
  useSignupIntent,
} from "@app/hooks";

import OtpHelpAccordion from "@app/components/auth/otpHelpAccordion/otpHelpAccordion";
import "@app/pages/private/setupProfile/setupProfile.scss";
import "./activation.scss";

const messages = defineMessages({
  activationConfirmNewAccountTitle: {
    id: "activationConfirmNewAccountTitle",
    defaultMessage: "Enter your code sent to you at {email}",
  },
  activationConfirmNewAccountDescription: {
    id: "activationConfirmNewAccountDescription",
    defaultMessage:
      "It may take up to a few minutes to get this email. If you don't see it in your inbox, please check your spam folder.",
  },
  activationResendTimeout: {
    id: "activationResendTimeout",
    defaultMessage: "Resend in {timeout}s",
  },
  activationResendCode: {
    id: "activationResendCode",
    defaultMessage: "Resend Code",
  },
  activationBack: {
    id: "activationBack",
    defaultMessage: "Back",
  },
  activationCodeInvalid: {
    id: "activationCodeInvalid",
    defaultMessage: "Wrong verification code. Please try again.",
  },
  activationCodeExpired: {
    id: "activationCodeExpired",
    defaultMessage:
      'This code has expired. Please click "Resend" to receive a new code.',
  },
  activationSignUpHelpContent: {
    id: "activationSignUpHelpContent",
    defaultMessage:
      "Please check whether your email address is spelled correctly.",
  },
  activationStep: {
    id: "step",
    defaultMessage: "Step {number} of {count}",
  },
});

import {
  ORGANIC_SIGN_UP_TOTAL_STEPS,
  TOKENIZED_SIGN_UP_TOTAL_STEPS,
  SIGN_UP_ACTIVATION_STEP,
  getSignUpFlowProgressValue,
} from "@app/utils/signUpFlowSteps";

const RESEND_COOLDOWN_SECONDS = 60;

const ActivationContent: FC = () => {
  const intl = useIntl();
  const { toLogin, toBack, toSignUp } = usePortalRedirects();
  const { token } = useTokenSourceFromQuery();
  // UPP-5865: effective handle (in-app proof or guest-flow `si`) so the OTP /
  // email-change completion carries and validates the DOB proof too.
  const { intentToken, handleReverificationRequired } = useSignupIntent();
  const { config } = useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);
  const {
    loadProfile,
    removeUserFromPasswordlessGroup,
    addUserToPasswordlessGroup,
  } = useContext(UserContext);
  const { sessionService } = useContext(ServiceContext);

  const {
    signUpData,
    setSignUpData,
    sendActivationCode,
    verifyEmailCode,
    verifyEmailCodeIsLoading,
  } = useContext(SignUpContext);

  const isOtpVerificationEnabled = config.otpVerification?.enabled;

  // verifyEmailCode is the actual account-creation step for the organic
  // signup flow — it provisions the Okta user with the submitted password.
  // Gate it behind reCAPTCHA so the only thing standing between an attacker
  // and a fully provisioned account is not just a 6-digit code.
  const {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    resetV2Challenge,
    v2Challenge,
    RecaptchaV3UnavailableError,
  } = useRecaptchaSubmit({
    action: "verifyEmailCode",
    v2ContainerClassName: "activation__recaptcha-v2",
    v2TestId: "activation-recaptcha-v2",
  });

  // The "Resend Code" button re-triggers the sendActivationCode mutation,
  // which the server verifies against the "signup" action — so it needs its
  // own token bound to that action (the hook above is "verifyEmailCode").
  const {
    acquireTokens: acquireResendTokens,
    handleRecaptchaError: handleResendRecaptchaError,
    promoteToV2Challenge: promoteResendToV2Challenge,
    resetV2Challenge: resetResendV2Challenge,
    v2Challenge: resendV2Challenge,
  } = useRecaptchaSubmit({
    action: "signup",
    v2ContainerClassName: "activation__recaptcha-v2",
    v2TestId: "activation-resend-recaptcha-v2",
  });

  // UI state
  const [resendSecondsLeft, setResendSecondsLeft] = useState(
    RESEND_COOLDOWN_SECONDS
  );
  const [localVerifyLoading, setLocalVerifyLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  // Mode comes from OtpVerificationContext (initialized from ActivationModeContext when navigating to this page)
  const { mode, ...otpVerification } = useOtpVerificationContext();
  const isSignInMode = mode === "signIn";
  const isSignInOtpFlow = isSignInMode && otpVerification;
  const isSignUpActivationFlow = !isSignInMode;
  const signUpTotalSteps = token
    ? TOKENIZED_SIGN_UP_TOTAL_STEPS
    : ORGANIC_SIGN_UP_TOTAL_STEPS;
  const signUpProgressValue = getSignUpFlowProgressValue(
    SIGN_UP_ACTIVATION_STEP,
    signUpTotalSteps
  );

  useEffect(() => {
    // Only check signUpData if we're in signUp mode
    if (!isSignInMode && !signUpData) {
      toLogin();
    }
  }, [signUpData, isSignInMode, toLogin]);

  const isLoading = verifyEmailCodeIsLoading || localVerifyLoading;
  const isOtpFlowLoading =
    otpVerification.isOtpRequestPending ||
    otpVerification.isOtpVerifyPending ||
    otpVerification.isRedirectingAfterVerify;
  const hasError = Boolean(errorText);

  // Cooldown timer
  useEffect(() => {
    if (resendSecondsLeft <= 0) return;

    const intervalId = window.setInterval(() => {
      setResendSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendSecondsLeft]);

  const email = signUpData?.email ?? "";
  const password = signUpData?.password ?? "";

  const startCooldown = useCallback(() => {
    setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
  }, []);

  const buildErrorText = useCallback(
    (verificationMessage?: VerificationMessageRes | string) => {
      if (verificationMessage === VerificationMessageRes.EXPIRED) {
        return intl.formatMessage(messages.activationCodeExpired);
      }
      return intl.formatMessage(messages.activationCodeInvalid);
    },
    [intl]
  );

  const handleActivation = useCallback(
    async (tokensOverride?: RecaptchaTokens): Promise<void> => {
      startCooldown();

      // signIn OTP resend is not reCAPTCHA-gated.
      if (isSignInOtpFlow && isOtpVerificationEnabled) {
        await addUserToPasswordlessGroup(otpVerification.otpEmail);
        await otpVerification.handleOtpResend();
        return;
      }

      if (!signUpData?.email) return;

      let captchaTokens: RecaptchaTokens;
      try {
        captchaTokens = tokensOverride ?? (await acquireResendTokens());
      } catch (err) {
        if (err instanceof RecaptchaV3UnavailableError) {
          promoteResendToV2Challenge(err.reason, async (tokens) => {
            await handleActivation(tokens);
          });
        }
        return;
      }

      try {
        await sendActivationCode({
          email: signUpData.email,
          language: currentLanguage,
          ...captchaTokens,
        });
        resetResendV2Challenge();
      } catch (err) {
        handleResendRecaptchaError(err, async (tokens) => {
          await handleActivation(tokens);
        });
      }
    },
    [
      isSignInOtpFlow,
      isOtpVerificationEnabled,
      addUserToPasswordlessGroup,
      otpVerification,
      signUpData?.email,
      currentLanguage,
      sendActivationCode,
      startCooldown,
      acquireResendTokens,
      promoteResendToV2Challenge,
      resetResendV2Challenge,
      handleResendRecaptchaError,
      RecaptchaV3UnavailableError,
    ]
  );

  const clearErrorOnTyping = useCallback(() => {
    if (errorText) setErrorText(undefined);
  }, [errorText]);

  const finishSignUpFlow = useCallback(async () => {
    try {
      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }

      await sessionService.login({ email, password });
      await loadProfile();
    } finally {
      setSignUpData(null);
    }
  }, [email, password, sessionService, loadProfile, setSignUpData]);

  const verifySignInOtp = useCallback(
    async (code: string): Promise<void> => {
      setLocalVerifyLoading(true);
      setErrorText(undefined);
      try {
        await otpVerification.handleOtpVerify(code);
      } catch {
        setErrorText(buildErrorText());
      } finally {
        setLocalVerifyLoading(false);
      }
    },
    [otpVerification, buildErrorText]
  );

  const verifyCode = useCallback(
    async (code: string, tokensOverride?: RecaptchaTokens): Promise<void> => {
      // In signUp mode we need signUpData (email/password); in signIn mode OTP state is in OtpVerificationProvider
      if (!isSignInMode && !signUpData) return;

      if (isSignInOtpFlow) {
        await verifySignInOtp(code);
        return;
      }

      setLocalVerifyLoading(true);
      setErrorText(undefined);

      let captchaTokens: RecaptchaTokens;
      try {
        captchaTokens = tokensOverride ?? (await acquireTokens());
      } catch (err) {
        setLocalVerifyLoading(false);
        if (err instanceof RecaptchaV3UnavailableError) {
          promoteToV2Challenge(err.reason, async (tokens) => {
            await verifyCode(code, tokens);
          });
          return;
        }
        setErrorText(buildErrorText());
        return;
      }

      try {
        const response = await verifyEmailCode({
          email,
          password,
          code,
          language: currentLanguage,
          ...(token ? { token } : {}),
          ...(intentToken ? { intentToken } : {}),
          ...captchaTokens,
        });

        const verification = response?.data?.verifyEmailCode;

        if (verification?.success === false) {
          setErrorText(buildErrorText(verification.message));
          return;
        }

        resetV2Challenge();
        await finishSignUpFlow();
      } catch (err) {
        const handled = handleRecaptchaError(err, async (tokens) => {
          await verifyCode(code, tokens);
        });
        if (handled) {
          return;
        }
        // UPP-5864: a rejected DOB proof on the OTP path routes back to sign-up
        // (DOB screen + banner) instead of surfacing as a "wrong code" error.
        if (handleReverificationRequired(err)) {
          toSignUp();
          return;
        }
        setErrorText(buildErrorText());
      } finally {
        setLocalVerifyLoading(false);
      }
    },
    [
      isSignInMode,
      signUpData,
      isSignInOtpFlow,
      verifySignInOtp,
      verifyEmailCode,
      email,
      password,
      currentLanguage,
      token,
      intentToken,
      buildErrorText,
      finishSignUpFlow,
      acquireTokens,
      handleRecaptchaError,
      handleReverificationRequired,
      toSignUp,
      promoteToV2Challenge,
      resetV2Challenge,
      RecaptchaV3UnavailableError,
    ]
  );

  const displayEmail = isSignInOtpFlow
    ? otpVerification.otpEmail
    : (signUpData?.email ?? "");

  const title: ReactElement = useMemo(
    () => (
      <p className="title__container">
        {intl.formatMessage(messages.activationConfirmNewAccountTitle, {
          email: displayEmail,
        })}
      </p>
    ),
    [intl, displayEmail]
  );

  const description: ReactElement = useMemo(
    () => (
      <p className="activation__text">
        {intl.formatMessage(messages.activationConfirmNewAccountDescription)}
      </p>
    ),
    [intl]
  );

  const errorMessage = isSignInOtpFlow
    ? otpVerification.otpErrorMessage
    : errorText;

  const codeInput: ReactElement = (
    <>
      <section className="activation__code-input">
        <CodeInput
          onCodeComplete={verifyCode}
          onCodeChange={
            isSignInOtpFlow
              ? otpVerification.handleOtpCodeChange
              : clearErrorOnTyping
          }
          hasError={hasError || !!errorMessage}
          errorMessage={errorMessage}
          codeFieldLength={6}
        />
      </section>
      <OtpHelpAccordion
        expiryMinutes={otpVerification.expiryMinutes}
        contentOverride={
          isSignInOtpFlow && !signUpData
            ? undefined
            : intl.formatMessage(messages.activationSignUpHelpContent)
        }
      />
    </>
  );

  const actions: ReactElement = (
    <section className="actions__container">
      <Button
        unelevated
        disabled={resendSecondsLeft > 0}
        onClick={() => handleActivation()}
      >
        {resendSecondsLeft > 0
          ? intl.formatMessage(messages.activationResendTimeout, {
              timeout: resendSecondsLeft,
            })
          : intl.formatMessage(messages.activationResendCode)}
      </Button>
      <div className="whitespace" />
      <Button
        onClick={isSignInOtpFlow ? otpVerification.handleOtpBack : toBack}
      >
        {intl.formatMessage(messages.activationBack)}
      </Button>
    </section>
  );

  if (isSignUpActivationFlow) {
    return (
      <article className="setup-profile__container">
        <Progress
          className="setup-profile__progress"
          value={signUpProgressValue}
        />
        <div className="setup-profile__wrapper">
          <section className="setup-profile__content activation__content">
            <h2 className="step-number">
              {intl.formatMessage(messages.activationStep, {
                number: SIGN_UP_ACTIVATION_STEP,
                count: signUpTotalSteps,
              })}
            </h2>
            {title}
            {description}
            {codeInput}
            {v2Challenge}
            {actions}
            <SpinnerView
              isLoading={isSignInOtpFlow ? isOtpFlowLoading : isLoading}
            />
          </section>
        </div>
      </article>
    );
  }

  return (
    <article className="activation__container">
      <div className="activation__content">
        {title}
        {description}
        {codeInput}
        {v2Challenge}
        {resendV2Challenge}
        {actions}
        <SpinnerView
          isLoading={isSignInOtpFlow ? isOtpFlowLoading : isLoading}
        />
      </div>
    </article>
  );
};

const Activation: FC = () => {
  // Read from sessionStorage so we get the value set right before navigate (context state may not have updated yet)
  const initialMode = getInitialMode();
  return (
    <OtpVerificationProvider initialMode={initialMode}>
      <ActivationContent />
    </OtpVerificationProvider>
  );
};

export default Activation;
